/**
 * GDPR Art. 17 Compliance Widget — Admin Dashboard.
 *
 * Pre-aggregates the immutable erasure audit log into 6 KPIs and lets
 * super_admin download the formal multi-language PDF report (IT/EN/DE)
 * for Vercesi / Garante. The report is GLOBAL — not per customer.
 *
 * Endpoint contract:
 *   GET /api/admin/gdpr/compliance-report?window_from=&window_to=
 *   GET /api/admin/gdpr/compliance-report.pdf?language=it|en|de&window_from=&window_to=
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Download, Loader2, FileDown,
  Inbox, CheckCircle2, AlertTriangle, BookLock, MapPinOff, Hourglass, Gavel,
  Mail, Send,
} from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../context/AuthContext";

const PERIODS = [
  { id: "ytd", label: "Anno corrente" },
  { id: "12m", label: "Ultimi 12 mesi" },
  { id: "all", label: "Dall'inizio" },
];

const todayIso = () => new Date().toISOString();
const yearStartIso = () => new Date(new Date().getFullYear(), 0, 1).toISOString();
const monthsAgoIso = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString();
};
// 2026-01-01 — wallet platform launch reference; window "all" pre-launch.
const PROJECT_START = "2026-01-01T00:00:00.000Z";

const resolveWindow = (period) => {
  switch (period) {
    case "12m": return { window_from: monthsAgoIso(12), window_to: todayIso() };
    case "all": return { window_from: PROJECT_START, window_to: todayIso() };
    case "ytd":
    default: return { window_from: yearStartIso(), window_to: todayIso() };
  }
};

const Kpi = ({ icon: Icon, value, label, tone = "neutral", testid }) => {
  const tones = {
    neutral: "bg-[#0d0d0d] border-gray-800 text-gray-300",
    positive: "bg-lojack-blue-500/10 border-lojack-blue-500/30 text-lojack-blue-300",
    amber: "bg-lojack-clause-500/10 border-lojack-clause-500/30 text-lojack-clause-300",
    red: "bg-red-500/10 border-red-500/40 text-red-300",
  };
  const iconColor = {
    neutral: "text-gray-400",
    positive: "text-lojack-blue-400",
    amber: "text-lojack-clause-400",
    red: "text-red-400",
  };
  return (
    <div className={`border rounded-xl p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={iconColor[tone]} />
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      </div>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
};

export const GdprComplianceWidget = () => {
  const { getAuthHeader, user } = useAuth();
  const [period, setPeriod] = useState("ytd");
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // 'it' | 'en' | 'de'
  const [digestStatus, setDigestStatus] = useState(null);
  const [digestSending, setDigestSending] = useState(false);

  const isSuperAdmin = user?.role === "super_admin";
  const win = useMemo(() => resolveWindow(period), [period]);

  useEffect(() => {
    if (!isSuperAdmin) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/gdpr/compliance-report`,
          { params: win, ...getAuthHeader() },
        );
        if (mounted) setSnapshot(r.data);
      } catch (e) {
        console.warn("GDPR compliance widget load failed", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [isSuperAdmin, period]); // eslint-disable-line react-hooks/exhaustive-deps

  // Monthly digest status — once per widget mount
  useEffect(() => {
    if (!isSuperAdmin) return;
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/gdpr/compliance-digest/status`, getAuthHeader(),
        );
        if (mounted) setDigestStatus(r.data);
      } catch (e) {
        console.warn("GDPR digest status load failed", e);
      }
    })();
    return () => { mounted = false; };
  }, [isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isSuperAdmin) return null;

  const downloadPdf = async (language) => {
    setDownloading(language);
    try {
      const r = await axios.get(
        `${API}/admin/gdpr/compliance-report.pdf`,
        {
          params: { ...win, language },
          ...getAuthHeader(),
          responseType: "blob",
        },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `LoJack_GDPR_Compliance_Report_${win.window_to.slice(0, 10)}_${language.toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Report ${language.toUpperCase()} scaricato`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fallito");
    } finally {
      setDownloading(null);
    }
  };

  const sendDigestNow = async () => {
    const recipients = (digestStatus?.configured_recipients || []).join(", ") || "destinatari predefiniti";
    if (!window.confirm(`Invio digest mensile a: ${recipients}.\nProcedo?`)) return;
    setDigestSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/gdpr/compliance-digest/run-now?force=true`,
        null,
        getAuthHeader(),
      );
      if (r.data?.sent) {
        toast.success(`Digest inviato a ${r.data.recipients.join(", ")}`);
      } else {
        toast.error(r.data?.error || "Invio non riuscito");
      }
      // Refresh status
      const s = await axios.get(`${API}/admin/gdpr/compliance-digest/status`, getAuthHeader());
      setDigestStatus(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio digest");
    } finally {
      setDigestSending(false);
    }
  };

  const ss = snapshot?.self_service || {};
  const adm = snapshot?.admin_manual || {};
  const disp = snapshot?.disputes || {};
  const ctr = snapshot?.contracts || {};
  const ob = snapshot?.onboarding || {};
  const ac = snapshot?.accidental_cancellations || {};

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="gdpr-compliance-widget">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lojack-clause-500/15 text-lojack-clause-400 flex items-center justify-center">
            <Gavel size={20} />
          </div>
          <div>
            <h3 className="text-white font-bold">GDPR Art. 17 — Compliance Report</h3>
            <p className="text-gray-500 text-xs">
              Riepilogo richieste cancellazione · audit-ready per Vercesi / Garante
            </p>
          </div>
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-1 bg-[#0d0d0d] border border-gray-800 rounded-xl p-1" data-testid="gdpr-period-selector">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                period === p.id
                  ? "bg-lojack-blue-500/20 text-lojack-blue-300 border border-lojack-blue-500/40"
                  : "text-gray-400 hover:text-white border border-transparent"
              }`}
              data-testid={`gdpr-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-500 text-sm">
          <Loader2 size={16} className="animate-spin mr-2" /> Aggregazione log…
        </div>
      ) : !snapshot ? (
        <div className="text-gray-500 text-sm py-6 text-center">Snapshot non disponibile.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <Kpi icon={Inbox} value={ss.requests_issued || 0}
              label="Richieste self-service" tone="neutral"
              testid="gdpr-kpi-requests" />
            <Kpi icon={CheckCircle2} value={ss.requests_executed || 0}
              label="Eseguite" tone="positive"
              testid="gdpr-kpi-executed" />
            <Kpi icon={Hourglass} value={ss.requests_abandoned_or_expired || 0}
              label="Abbandonate" tone="neutral"
              testid="gdpr-kpi-abandoned" />
            <Kpi icon={BookLock} value={adm.executions || 0}
              label="Admin manuali" tone="amber"
              testid="gdpr-kpi-admin" />
            <Kpi icon={MapPinOff} value={disp.active_now || 0}
              label="Controversie attive"
              tone={(disp.active_now || 0) > 0 ? "amber" : "neutral"}
              testid="gdpr-kpi-disputes" />
            <Kpi icon={AlertTriangle}
              value={ctr.overdue_retention_at_window_end || 0}
              label="Contratti oltre retention"
              tone={(ctr.overdue_retention_at_window_end || 0) > 0 ? "red" : "neutral"}
              testid="gdpr-kpi-overdue" />
          </div>

          {/* Response time mini-summary */}
          <div className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-3 mb-4 flex items-center justify-between flex-wrap gap-2 text-xs">
            <div className="text-gray-400">
              Tempo mediano di risposta self-service:{" "}
              <strong className="text-white">
                {ss.median_response_minutes != null ? `${ss.median_response_minutes.toFixed(1)} min` : "—"}
              </strong>
              <span className="text-gray-600"> · p90: </span>
              <strong className="text-white">
                {ss.p90_response_minutes != null ? `${ss.p90_response_minutes.toFixed(1)} min` : "—"}
              </strong>
            </div>
            <div className="text-gray-500">
              Periodo: <span className="text-gray-300 font-mono">{snapshot.window.from.slice(0, 10)}</span>
              {" → "}
              <span className="text-gray-300 font-mono">{snapshot.window.to.slice(0, 10)}</span>
            </div>
          </div>

          {/* Vercesi operational metrics — onboarding + accidental cancellations */}
          <div className="bg-[#0d0d0d] border border-lojack-clause-500/20 rounded-xl p-4 mb-4" data-testid="gdpr-operational-panel">
            <div className="flex items-center gap-2 mb-3">
              <Gavel size={13} className="text-lojack-clause-400" />
              <p className="text-xs uppercase tracking-wider text-lojack-clause-300 font-semibold">Metriche operative (Vercesi)</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3" data-testid="gdpr-onboarding-kpis">
              <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-2.5">
                <p className="text-[10px] uppercase text-gray-500">Clienti nel periodo</p>
                <p className="text-lg font-bold text-white tabular-nums">{ob.total_customers_in_window || 0}</p>
              </div>
              <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-2.5">
                <p className="text-[10px] uppercase text-gray-500">Tasso firma</p>
                <p className="text-lg font-bold text-lojack-blue-300 tabular-nums">
                  {ob.signature_rate_pct != null ? `${ob.signature_rate_pct}%` : "—"}
                </p>
              </div>
              <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-2.5">
                <p className="text-[10px] uppercase text-gray-500">Mediana creazione→firma</p>
                <p className="text-lg font-bold text-white tabular-nums">
                  {ob.time_to_signature_minutes?.median != null ? `${ob.time_to_signature_minutes.median} min` : "—"}
                </p>
              </div>
              <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-2.5">
                <p className="text-[10px] uppercase text-gray-500">Aperto anteprima</p>
                <p className="text-lg font-bold text-white tabular-nums">
                  {ob.preview_rate_pct != null ? `${ob.preview_rate_pct}%` : "—"}
                </p>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs" data-testid="gdpr-accidental-kpis">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Annullate dal cliente:</span>
                <strong className="text-lojack-clause-200">{ac.tokens_revoked_by_customer || 0}</strong>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Recovery rate:</span>
                <strong className="text-lojack-clause-200">{ac.recovery_rate_pct != null ? `${ac.recovery_rate_pct}%` : "—"}</strong>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Mediana ripensamento:</span>
                <strong className="text-lojack-clause-200">{ac.time_to_revoke_seconds?.median != null ? `${ac.time_to_revoke_seconds.median} s` : "—"}</strong>
              </div>
            </div>
          </div>

          {/* Monthly digest panel */}
          {digestStatus && (
            <div className="bg-[#0d0d0d] border border-lojack-clause-500/20 rounded-xl p-3 mb-4" data-testid="gdpr-digest-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2 min-w-0">
                  <Mail size={14} className="text-lojack-clause-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold">Digest mensile automatico</p>
                    <p className="text-gray-400 text-xs mt-0.5">
                      Invio il 1° di ogni mese alle 09:00 CET a{" "}
                      <strong className="text-lojack-clause-200">
                        {(digestStatus.configured_recipients || []).join(", ") || "—"}
                      </strong>
                    </p>
                    {digestStatus.last_run_at && (
                      <p className="text-gray-500 text-[11px] mt-1">
                        Ultimo invio: <span className="font-mono text-gray-400">{digestStatus.last_run_at.slice(0, 19).replace("T", " ")}</span>
                        {digestStatus.last_period_label && <> · periodo <strong className="text-gray-300">{digestStatus.last_period_label}</strong></>}
                        {digestStatus.last_sent === true && <span className="text-lojack-blue-300 ml-1.5">✓ consegnato</span>}
                        {digestStatus.last_sent === false && <span className="text-red-300 ml-1.5">✗ {digestStatus.last_error || "errore"}</span>}
                      </p>
                    )}
                    {!digestStatus.last_run_at && (
                      <p className="text-gray-500 text-[11px] mt-1 italic">Mai eseguito — il primo invio partirà al prossimo cron.</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={sendDigestNow}
                  disabled={digestSending}
                  className="px-3 py-2 bg-lojack-blue-500/15 hover:bg-lojack-blue-500/25 disabled:opacity-50 text-lojack-blue-300 border border-lojack-blue-500/40 rounded-xl flex items-center gap-2 text-xs font-medium shrink-0"
                  data-testid="gdpr-digest-send-now"
                >
                  {digestSending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Invia ora (test)
                </button>
              </div>
            </div>
          )}

          {/* Download buttons */}
          <div className="flex items-center justify-between flex-wrap gap-2 border-t border-gray-800 pt-4">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <ShieldCheck size={14} className="text-lojack-blue-400" />
              <span>
                PDF formale firmato TelcaVoIP — log immutabili gdpr_erasure_log
              </span>
            </div>
            <div className="flex items-center gap-2" data-testid="gdpr-report-downloads">
              {["it", "en", "de"].map((lang) => (
                <button
                  key={lang}
                  onClick={() => downloadPdf(lang)}
                  disabled={downloading !== null}
                  className="px-3 py-2 bg-lojack-clause-500/15 hover:bg-lojack-clause-500/25 disabled:opacity-50 text-lojack-clause-300 border border-lojack-clause-500/40 rounded-xl flex items-center gap-2 text-xs font-medium transition-colors"
                  data-testid={`gdpr-report-dl-${lang}`}
                >
                  {downloading === lang
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Download size={12} />}
                  PDF {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Stale state hint */}
          {(adm.executions || 0) === 0 && (ss.requests_issued || 0) === 0 && (
            <div className="mt-3 text-[11px] text-gray-600 italic flex items-center gap-1.5">
              <FileDown size={11} /> Nessuna attività GDPR Art. 17 nel periodo selezionato.
              Il report viene comunque generato vuoto, utile per certificare l'assenza di richieste.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GdprComplianceWidget;
